import { Injectable } from '@angular/core';
import {
  FHIRPatient,
  FHIRPractitioner,
  FHIREncounter,
  FHIRCondition,
  FHIRAllergyIntolerance,
  FHIRImmunization,
  FHIRExtension,
  PatientData,
  PractitionerData,
  EncounterData,
  ConditionData,
  AllergyData,
  ImmunizationData,
} from '../../shared/models';

@Injectable({
  providedIn: 'root',
})
export class FhirMapperService {
  /**
   * Extrae una extensión por patrón de URL
   */
  private getExtension(
    extensions: FHIRExtension[] | undefined,
    urlPattern: string,
  ): FHIRExtension | undefined {
    return extensions?.find((e) => e.url?.includes(urlPattern));
  }

  /**
   * Calcula edad desde fecha de nacimiento
   */
  private calcularEdad(birthDateStr: string | undefined): string | number {
    if (!birthDateStr) return 'N/A';
    const birth = new Date(birthDateStr);
    if (isNaN(birth.getTime())) return 'N/A';

    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age < 0 ? 'N/A' : age;
  }

  /**
   * Mapea Patient FHIR a PatientData
   */
  mapPatient(patient: FHIRPatient | undefined): PatientData | null {
    if (!patient) return null;

    const tipoDocCoding =
      patient.identifier?.[0]?.type?.coding?.find((c) =>
        c.system?.includes('ColombianPersonIdentifier'),
      ) || patient.identifier?.[0]?.type?.coding?.[0];

    const address0 = patient.address?.[0];
    const name0 = patient.name?.[0];

    const nombres = `${(name0?.given || []).join(' ')} ${name0?.family || ''}`.trim();

    return {
      activo: patient.active === true,
      fallecido: patient.deceasedBoolean === true,
      nombres,
      documento: patient.identifier?.[0]?.value || '',
      tipoDocumento: tipoDocCoding
        ? {
            code: tipoDocCoding.code?.toUpperCase() || '',
            display: tipoDocCoding.display || '',
          }
        : null,
      fechaNacimiento: patient.birthDate || '',
      horaNacimiento:
        this.getExtension(patient._birthDate?.extension, 'ExtensionBirthTime')?.valueTime || '',
      edad: this.calcularEdad(patient.birthDate),
      sexoBiologico: patient.gender || '',
      sexoBiologicoExt:
        this.getExtension(patient._gender?.extension, 'ExtensionBiologicalGender')?.valueCoding
          ?.display || '',
      identidadGenero:
        this.getExtension(patient.extension, 'ExtensionPatientGenderIdentity')?.valueCoding
          ?.display || '',
      nacionalidad:
        this.getExtension(patient.extension, 'ExtensionPatientNationality')?.valueCoding?.display ||
        '',
      etnia:
        this.getExtension(patient.extension, 'ExtensionPatientEthnicity')?.valueCoding?.display ||
        '',
      discapacidad:
        this.getExtension(patient.extension, 'ExtensionPatientDisability')?.valueCoding?.display ||
        '',
      paisResidencia: address0?.country || '',
      municipioResidencia: address0?.city || '',
      zonaResidencia:
        this.getExtension(address0?.extension, 'ExtensionResidenceZone')?.valueCoding?.display ||
        '',
      divipolaMunicipio:
        this.getExtension(address0?._city?.extension, 'ExtensionDivipolaMunicipality')?.valueCoding
          ?.code || '',
      codigoPais:
        this.getExtension(address0?._country?.extension, 'ExtensionCountryCode')?.valueCoding
          ?.code || '',
    };
  }

  /**
   * Mapea Practitioner FHIR a PractitionerData
   */
  mapPractitioner(practitioner: FHIRPractitioner | undefined): PractitionerData | null {
    if (!practitioner) return null;

    const tipoDocCoding =
      practitioner.identifier?.[0]?.type?.coding?.find((c) =>
        c.system?.includes('ColombianPersonIdentifier'),
      ) || practitioner.identifier?.[0]?.type?.coding?.[0];

    const name0 = practitioner.name?.[0];
    const apellidoPaterno =
      this.getExtension(name0?._family?.extension, 'ExtensionFathersFamilyName')?.valueString || '';
    const apellidoMaterno =
      this.getExtension(name0?._family?.extension, 'ExtensionMothersFamilyName')?.valueString || '';
    const apellidoCompleto = name0?.family || `${apellidoPaterno} ${apellidoMaterno}`.trim();
    const nombres = (name0?.given || []).join(' ');

    return {
      id: practitioner.id || '',
      documento: practitioner.identifier?.[0]?.value || '',
      tipoDocumento: tipoDocCoding
        ? {
            code: tipoDocCoding.code?.toUpperCase() || '',
            display: tipoDocCoding.display || '',
          }
        : null,
      nombres,
      apellidos: apellidoCompleto,
      apellidoPaterno,
      apellidoMaterno,
      nombreCompleto: `${nombres} ${apellidoCompleto}`.trim(),
      activo: practitioner.active === true,
      genero: practitioner.gender || '',
      fechaNacimiento: practitioner.birthDate || '',
      telecom: (practitioner.telecom || []).map((t) => ({
        sistema: t.system || '',
        valor: t.value || '',
        uso: t.use || '',
      })),
      direccion: (practitioner.address || []).map((a) => ({
        uso: a.use || '',
        tipo: a.type || '',
        lineas: a.line || [],
        ciudad: a.city || '',
        estado: a.state || '',
        codigoPostal: a.postalCode || '',
        pais: a.country || '',
      })),
      qualification: (practitioner.qualification || []).map((q) => ({
        identificador: q.identifier?.[0]?.value || '',
        codigo: q.code?.coding?.[0]?.code || '',
        display: q.code?.coding?.[0]?.display || '',
        emisor: q.issuer?.display || q.issuer?.reference || '',
        periodo: {
          inicio: q.period?.start || '',
          fin: q.period?.end || '',
        },
      })),
      foto: practitioner.photo?.[0]?.url || '',
      communication: (practitioner.communication || []).map(
        (c) => c.coding?.[0]?.display || c.text || '',
      ),
    };
  }

  /**
   * Mapea Encounter FHIR a EncounterData
   */
  mapEncounter(encounter: FHIREncounter | undefined): EncounterData | null {
    if (!encounter) return null;

    const getCodingFromType = (t: any) => {
      const c = t?.coding;
      if (Array.isArray(c)) return c[0];
      if (c && typeof c === 'object') return c;
      return null;
    };

    const participantes = (encounter.participant || []).map((p) => {
      const tipoArr = Array.isArray(p.type?.[0]?.coding)
        ? p.type[0].coding
        : p.type?.[0]?.coding
          ? [p.type[0].coding]
          : [];
      const tipoCoding = tipoArr[0];
      return {
        id: p.id || '',
        rolCodigo: tipoCoding?.code || '',
        rolDisplay: tipoCoding?.display || '',
        referencia: p.individual?.reference || '',
      };
    });

    const diagnosticos = (encounter.diagnosis || []).map((d) => {
      const useCoding = Array.isArray(d.use?.coding) ? d.use.coding[0] : d.use?.coding;
      const extTipo = (d.extension || []).find((e) => e.url?.includes('ExtensionDiagnosisType'));
      return {
        id: d.id || '',
        rank: d.rank ?? null,
        rol: useCoding?.display || useCoding?.code || '',
        rolCodigo: useCoding?.code || '',
        tipoDx: extTipo?.valueCoding?.display || '',
        tipoDxCodigo: extTipo?.valueCoding?.code || '',
        condicion: d.condition?.reference || '',
      };
    });

    const hosp = encounter.hospitalization;
    const hospitalizacion = hosp
      ? {
          estadoFallecimiento:
            (hosp.extension || []).find((e) => e.url?.includes('DischargeDeceasedStatus'))
              ?.valueCoding?.display || '',
          viaIngreso: hosp.admitSource?.coding?.[0]?.display || '',
          viaIngresoCodigo: hosp.admitSource?.coding?.[0]?.code || '',
          condicionEgreso: hosp.dischargeDisposition?.coding?.[0]?.display || '',
          condicionEgresoCodigo: hosp.dischargeDisposition?.coding?.[0]?.code || '',
          destino: hosp.destination?.reference || '',
          reingreso: hosp.reAdmission?.coding?.[0]?.display || '',
        }
      : null;

    return {
      id: encounter.id || '',
      status: encounter.status || '',
      identificador: encounter.identifier?.[0]?.value || '',
      clase: {
        codigo: encounter.class?.code || '',
        display: encounter.class?.display || '',
      },
      tipos: (encounter.type || [])
        .map((t) => {
          const c = getCodingFromType(t);
          return {
            codigo: c?.code || '',
            display: c?.display || '',
            sistema: c?.system || '',
          };
        })
        .filter((t) => t.codigo || t.display),
      periodo: {
        inicio: encounter.period?.start || '',
        fin: encounter.period?.end || '',
      },
      motivos: (encounter.reasonCode || []).map((rc) => {
        const c = Array.isArray(rc.coding) ? rc.coding[0] : rc.coding;
        return {
          codigo: c?.code || '',
          display: c?.display || '',
          text: rc.text || '',
        };
      }),
      participantes,
      diagnosticos,
      hospitalizacion,
      ubicaciones: (encounter.location || []).map((l) => ({
        display: l.location?.display || '',
        referencia: l.location?.reference || '',
        estado: l.status || '',
      })),
      prestador: encounter.serviceProvider?.reference || '',
    };
  }

  /**
   * Mapea Condition FHIR a ConditionData
   */
  mapCondition(condition: FHIRCondition | undefined): ConditionData | null {
    if (!condition) return null;

    const clinicalStatusCoding = condition.clinicalStatus?.coding?.[0];
    const verificationStatusCoding = condition.verificationStatus?.coding?.[0];
    const categoryCoding = condition.category?.[0]?.coding?.[0];
    const codeCoding = condition.code?.coding?.[0];
    const severityCoding = condition.severity?.coding?.[0];

    return {
      id: condition.id || '',
      estadoClinico: {
        codigo: clinicalStatusCoding?.code || '',
        display: clinicalStatusCoding?.display || '',
        sistema: clinicalStatusCoding?.system || '',
      },
      estadoVerificacion: {
        codigo: verificationStatusCoding?.code || '',
        display: verificationStatusCoding?.display || '',
        sistema: verificationStatusCoding?.system || '',
      },
      categoria: {
        codigo: categoryCoding?.code || '',
        display: categoryCoding?.display || '',
        sistema: categoryCoding?.system || '',
      },
      severidad: severityCoding
        ? {
            codigo: severityCoding.code || '',
            display: severityCoding.display || '',
            sistema: severityCoding.system || '',
          }
        : null,
      codigo: {
        codigo: codeCoding?.code || '',
        display: codeCoding?.display || '',
        sistema: codeCoding?.system || '',
        text: condition.code?.text || '',
      },
      bodySite: (condition.bodySite || []).map((bs) => ({
        codigo: bs.coding?.[0]?.code || '',
        display: bs.coding?.[0]?.display || '',
        text: bs.text || '',
      })),
      sujeto: condition.subject?.reference || '',
      encounter: condition.encounter?.reference || '',
      fechaInicio: condition.onsetDateTime || condition.onsetPeriod?.start || '',
      fechaFin: condition.abatementDateTime || condition.abatementPeriod?.end || '',
      fechaRegistro: condition.recordedDate || '',
      registradoPor: condition.recorder?.reference || '',
      aseveradoPor: condition.asserter?.reference || '',
      stage: (condition.stage || []).map((s) => ({
        resumen: s.summary?.coding?.[0]?.display || s.summary?.text || '',
        tipo: s.type?.coding?.[0]?.display || '',
      })),
      evidence: (condition.evidence || []).map((e) => ({
        codigo: e.code?.[0]?.coding?.[0]?.display || '',
        detalle: (e.detail || []).map((d) => d.reference || ''),
      })),
      notas: (condition.note || []).map((n) => n.text || '').join(' | '),
    };
  }

  /**
   * Mapea AllergyIntolerance FHIR a AllergyData
   */
  mapAllergy(allergy: FHIRAllergyIntolerance | undefined): AllergyData | null {
    if (!allergy) return null;

    const clinicalStatusCoding = allergy.clinicalStatus?.coding?.[0];
    const verificationCoding = allergy.verificationStatus?.coding?.[0];
    const tipoAlergiaCoding = allergy.code?.coding?.[0];

    return {
      id: allergy.id || '',
      estadoClinico: {
        codigo: clinicalStatusCoding?.code || '',
        display: clinicalStatusCoding?.display || '',
      },
      estadoVerificacion: verificationCoding
        ? {
            codigo: verificationCoding.code || '',
            display: verificationCoding.display || '',
          }
        : null,
      tipoAlergia: {
        codigo: tipoAlergiaCoding?.code || '',
        display: tipoAlergiaCoding?.display || '',
        sistema: tipoAlergiaCoding?.system || '',
      },
      descripcion: allergy.code?.text || '',
      tipo: allergy.type || '',
      categorias: allergy.category || [],
      criticidad: allergy.criticality || '',
      paciente: allergy.patient?.reference || '',
      encounter: allergy.encounter?.reference || '',
      fechaInicio: allergy.onsetDateTime || allergy.onsetPeriod?.start || '',
      fechaRegistro: allergy.recordedDate || '',
      registradoPor: allergy.recorder?.reference || '',
      aseveradoPor: allergy.asserter?.reference || '',
      reacciones: (allergy.reaction || []).map((r) => ({
        sustancia: r.substance?.coding?.[0]?.display || '',
        manifestaciones: (r.manifestation || []).map((m) => m.coding?.[0]?.display || ''),
        descripcion: r.description || '',
        inicio: r.onset || '',
        severidad: r.severity || '',
      })),
      notas: (allergy.note || []).map((n) => n.text || '').join(' | '),
    };
  }

  /**
   * Mapea Immunization FHIR a ImmunizationData
   */
  mapImmunization(immunization: FHIRImmunization | undefined): ImmunizationData | null {
    if (!immunization) return null;

    return {
      vacuna:
        immunization.vaccineCode?.text || immunization.vaccineCode?.coding?.[0]?.display || '',
      fecha: immunization.occurrenceDateTime || immunization.occurrenceString || '',
      lote: immunization.lotNumber || '',
      estado: immunization.status || '',
      prestador:
        (immunization.performer || [])
          .map((p) => p.actor?.display)
          .filter(Boolean)
          .join(' / ') || '',
      fabricante: immunization.manufacturer?.display || '',
    };
  }

  /**
   * Extrae inmunizaciones de un Bundle
   */
  extractImmunizations(bundle: any): ImmunizationData[] {
    if (!bundle) return [];

    const immunizations: FHIRImmunization[] = [];

    if (bundle.resourceType === 'Bundle' && Array.isArray(bundle.entry)) {
      for (const entry of bundle.entry) {
        const resource = entry.resource;
        if (resource?.resourceType === 'Immunization') {
          immunizations.push(resource);
        }
      }
    } else if (bundle.resourceType === 'Immunization') {
      immunizations.push(bundle);
    }

    return immunizations
      .map((imm) => this.mapImmunization(imm))
      .filter((data): data is ImmunizationData => data !== null);
  }
}
